import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, Check, CheckCheck, BookOpen, MessageSquare, Video, FileText, Star, Megaphone, Wifi, WifiOff } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import api from '../utils/api';

const Header = ({ title, onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openMenu, closeMenu, isMobileOpen } = useMobileMenu();

  // Always drive the drawer from MobileMenuContext (works even when DashboardRouter
  // did not forward onMenuClick). Prop kept only so pages can still pass it.
  const handleMenuClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isMobileOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };
  // silence unused prop lint when pages still pass onMenuClick
  void onMenuClick;
  const { 
    isConnected, 
    unreadCount, 
    notifications: wsNotifications,
    markNotificationRead,
    updateUnreadCount,
    setNotificationsList 
  } = useWebSocket();
  
  const [notifications, setNotifications] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Sync WebSocket notifications with local state
  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications(prev => {
        // Merge new notifications, avoiding duplicates
        const existingIds = new Set(prev.map(n => n.id));
        const newNotifs = wsNotifications.filter(n => !existingIds.has(n.id));
        return [...newNotifs, ...prev].slice(0, 50);
      });
    }
  }, [wsNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      setNotificationsList(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownToggle = () => {
    if (!dropdownOpen) {
      fetchNotifications();
    }
    setDropdownOpen(!dropdownOpen);
  };

  const markAsRead = async (notifId) => {
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, is_read: true } : n)
      );
      markNotificationRead(notifId);
      updateUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      updateUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'nouveau_devoir':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'devoir_corrige':
        return <Check className="w-4 h-4 text-emerald-500" />;
      case 'evaluation_notee':
        return <Star className="w-4 h-4 text-amber-500" />;
      case 'nouveau_message':
      case 'message':
        return <MessageSquare className="w-4 h-4 text-violet-500" />;
      case 'live_a_venir':
        return <Video className="w-4 h-4 text-rose-500" />;
      case 'mise_a_jour_cours':
        return <BookOpen className="w-4 h-4 text-indigo-500" />;
      case 'nouvelle_annonce':
        return <Megaphone className="w-4 h-4 text-orange-500" />;
      case 'nouveau_document':
        return <FileText className="w-4 h-4 text-cyan-500" />;
      case 'emargement_ouvert':
        return <CheckCheck className="w-4 h-4 text-emerald-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <header className="glass-header sticky top-0 z-[70] px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
      <div className="flex items-center justify-between gap-2 sm:gap-4 min-w-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          {/* Native button: more reliable than shadcn Button for touch on Android Chrome */}
          <button
            type="button"
            className="md:hidden shrink-0 relative z-[95] pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-md text-indigo-950 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 touch-manipulation select-none"
            onClick={handleMenuClick}
            aria-label={isMobileOpen ? 'Fermer le menu de navigation' : 'Ouvrir le menu de navigation'}
            aria-expanded={isMobileOpen}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-indigo-950 dark:text-white font-outfit truncate">
              {title}
            </h1>
            <p className="hidden sm:block text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-0.5 truncate">
              {user?.role === 'super_admin' && 'Administration Plateforme'}
              {user?.role === 'cfa_admin' && 'Administration CFA'}
              {user?.role === 'trainer' && 'Espace Formateur'}
              {user?.role === 'student' && 'Espace Étudiant'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-3 relative shrink-0" ref={dropdownRef}>
          {/* WebSocket Connection Indicator */}
          <div className="hidden sm:flex items-center gap-1 text-xs">
            {isConnected ? (
              <Wifi className="w-3 h-3 text-emerald-500" />
            ) : (
              <WifiOff className="w-3 h-3 text-slate-400" />
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={handleDropdownToggle}
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-rose-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1 animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>

          {/* Notifications Dropdown — viewport-safe width on mobile */}
          {dropdownOpen && (
            <div
              className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[4.25rem] sm:top-full mt-0 sm:mt-2 w-auto sm:w-96 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
              data-testid="notifications-dropdown"
            >
              <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white truncate">Notifications</h3>
                  {isConnected && (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full shrink-0">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                      Live
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs shrink-0 px-2">
                    <CheckCheck className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">Tout marquer lu</span>
                  </Button>
                )}
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <Bell className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucune notification</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-700">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${
                          !notif.is_read ? 'bg-violet-50/50 dark:bg-violet-900/10' : ''
                        }`}
                        onClick={() => !notif.is_read && markAsRead(notif.id)}
                        data-testid={`notification-item-${notif.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                            {getNotificationIcon(notif.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm ${!notif.is_read ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                                {notif.title}
                              </p>
                              {!notif.is_read && (
                                <span className="w-2 h-2 bg-violet-600 rounded-full flex-shrink-0"></span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                              {notif.message}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {formatDate(notif.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {notifications.length > 0 && (
                <div className="p-3 border-t border-slate-200 dark:border-slate-700">
                  <Button
                    variant="ghost"
                    className="w-full text-sm text-violet-600 hover:text-violet-700"
                    onClick={() => { setDropdownOpen(false); navigate('/notifications'); }}
                  >
                    Voir toutes les notifications
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
